import React, { useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, FormGroup, Label, Input, Button, Alert, Table, Badge, Progress, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

export default function SkilledMigrationCalculator() {
  const [activeTab, setActiveTab] = useState('1');
  const [formData, setFormData] = useState({
    // Personal Details
    age: '',
    englishTest: '',
    englishScore: {
      listening: '',
      reading: '',
      writing: '',
      speaking: ''
    },

    // Work Experience
    skilledEmploymentAus: '',
    skilledEmploymentOverseas: '',

    // Education
    educationLevel: '',
    studyInAustralia: false,
    australianStudyYears: '',
    specialistQualification: false,

    // Additional Points
    naatiCredential: false,
    professionalYear: false,
    regionalStudy: false,

    // Relationship Status
    relationshipStatus: '',
    partnerEnglish: '',
    partnerSkillsAssessment: false,

    // Nomination
    nominationType: ''
  });

  const [totalPoints, setTotalPoints] = useState(0);
  const [breakdown, setBreakdown] = useState([]);
  const [englishLevel, setEnglishLevel] = useState('');
  const [recommendations, setRecommendations] = useState([]);

  const pointsTable = {
    age: {
      '18-24': 25,
      '25-32': 30,
      '33-39': 25,
      '40-44': 15,
      '45-49': 0
    },
    english: {
      'competent': 0,
      'proficient': 10,
      'superior': 20
    },
    ausWork: {
      'none': 0,
      '1-2': 5,
      '3-4': 10,
      '5-7': 15,
      '8+': 20
    },
    overseasWork: {
      'none': 0,
      '3-4': 5,
      '5-7': 10,
      '8+': 15
    },
    education: {
      'diploma': 10,
      'bachelor': 15,
      'masters-doctorate': 20
    },
    nomination: {
      'none': 0,
      'state-190': 5,
      'state-491': 15,
      'family-491': 15
    }
  };

  const determineEnglishLevel = (scores, testType) => {
    if (!scores.listening || !scores.reading || !scores.writing || !scores.speaking) {
      return { level: '', points: 0 };
    }

    const l = parseFloat(scores.listening);
    const r = parseFloat(scores.reading);
    const w = parseFloat(scores.writing);
    const s = parseFloat(scores.speaking);

    if (testType === 'ielts') {
      if (l >= 8 && r >= 8 && w >= 8 && s >= 8) return { level: 'superior', points: 20 };
      if (l >= 7 && r >= 7 && w >= 7 && s >= 7) return { level: 'proficient', points: 10 };
      if (l >= 6 && r >= 6 && w >= 6 && s >= 6) return { level: 'competent', points: 0 };
    } else if (testType === 'pte') {
      if (l >= 79 && r >= 79 && w >= 79 && s >= 79) return { level: 'superior', points: 20 };
      if (l >= 65 && r >= 65 && w >= 65 && s >= 65) return { level: 'proficient', points: 10 };
      if (l >= 50 && r >= 50 && w >= 50 && s >= 50) return { level: 'competent', points: 0 };
    } else if (testType === 'toefl') {
      if (l >= 24 && r >= 24 && w >= 27 && s >= 23) return { level: 'superior', points: 20 };
      if (l >= 12 && r >= 13 && w >= 21 && s >= 18) return { level: 'proficient', points: 10 };
      if (l >= 4 && r >= 4 && w >= 14 && s >= 14) return { level: 'competent', points: 0 };
    }

    return { level: '', points: 0 };
  };

  const generateRecommendations = (points, data) => {
    const recs = [];

    if (points < 65) {
      recs.push({
        title: '🎯 Improve English Score',
        description: 'Achieving Superior English (IELTS 8.0 each) can add up to 20 points.',
        priority: 'high'
      });
    }

    if (!data.professionalYear && data.skilledEmploymentAus) {
      recs.push({
        title: '👔 Complete Professional Year',
        description: 'Professional Year programs in IT, Accounting, or Engineering add 5 points.',
        priority: 'medium'
      });
    }

    if (!data.naatiCredential) {
      recs.push({
        title: '🌐 NAATI Credential',
        description: 'Gain 5 points by obtaining NAATI credentialed community language credential.',
        priority: 'medium'
      });
    }

    if (data.nominationType === 'none' && points >= 60) {
      recs.push({
        title: '🏛️ State Nomination',
        description: 'Consider applying for state nomination (190/491) to add 5-15 points.',
        priority: 'high'
      });
    }

    if (points >= 65 && points < 75) {
      recs.push({
        title: '📈 Boost Your Score',
        description: 'Aim for 75+ points for more competitive invitation chances.',
        priority: 'medium'
      });
    }

    return recs;
  };

  const calculatePoints = () => {
    let points = 0;
    let details = [];

    // Age
    if (formData.age) {
      const agePoints = pointsTable.age[formData.age];
      points += agePoints;
      if (agePoints > 0) details.push({ category: 'Age', points: agePoints, icon: '👤' });
    }

    // English Proficiency
    if (formData.englishTest) {
      const englishResult = determineEnglishLevel(formData.englishScore, formData.englishTest);
      if (englishResult.points > 0) {
        points += englishResult.points;
        setEnglishLevel(englishResult.level);
        details.push({
          category: `English (${englishResult.level})`,
          points: englishResult.points,
          icon: '🗣️'
        });
      }
    }

    // Australian Work Experience
    if (formData.skilledEmploymentAus) {
      const ausWorkPoints = pointsTable.ausWork[formData.skilledEmploymentAus];
      points += ausWorkPoints;
      if (ausWorkPoints > 0) {
        details.push({ category: 'Australian Work Experience', points: ausWorkPoints, icon: '💼' });
      }
    }

    // Overseas Work Experience
    if (formData.skilledEmploymentOverseas) {
      const overWorkPoints = pointsTable.overseasWork[formData.skilledEmploymentOverseas];
      points += overWorkPoints;
      if (overWorkPoints > 0) {
        details.push({ category: 'Overseas Work Experience', points: overWorkPoints, icon: '🌏' });
      }
    }

    // Education
    if (formData.educationLevel) {
      const eduPoints = pointsTable.education[formData.educationLevel];
      points += eduPoints;
      details.push({ category: 'Education Qualification', points: eduPoints, icon: '🎓' });
    }

    // Australian Study
    if (formData.studyInAustralia && formData.australianStudyYears === '2+') {
      points += 5;
      details.push({ category: 'Australian Study Requirement', points: 5, icon: '📚' });
    }

    // Specialist Education
    if (formData.specialistQualification) {
      points += 10;
      details.push({ category: 'Specialist Education (STEM)', points: 10, icon: '🔬' });
    }

    // NAATI
    if (formData.naatiCredential) {
      points += 5;
      details.push({ category: 'NAATI Credential', points: 5, icon: '🌐' });
    }

    // Professional Year
    if (formData.professionalYear) {
      points += 5;
      details.push({ category: 'Professional Year', points: 5, icon: '👔' });
    }

    // Regional Study
    if (formData.regionalStudy) {
      points += 5;
      details.push({ category: 'Regional Australia Study', points: 5, icon: '🏞️' });
    }

    // Partner Points
    if (formData.relationshipStatus === 'partner') {
      if (formData.partnerSkillsAssessment && formData.partnerEnglish === 'competent') {
        points += 10;
        details.push({ category: 'Partner Skills', points: 10, icon: '👥' });
      } else if (formData.partnerEnglish === 'competent') {
        points += 5;
        details.push({ category: 'Partner English', points: 5, icon: '👥' });
      }
    } else if (formData.relationshipStatus === 'single') {
      points += 10;
      details.push({ category: 'Single Applicant', points: 10, icon: '👤' });
    }

    // Nomination
    if (formData.nominationType) {
      const nomPoints = pointsTable.nomination[formData.nominationType];
      points += nomPoints;
      if (nomPoints > 0) {
        details.push({ category: 'State/Territory Nomination', points: nomPoints, icon: '🏛️' });
      }
    }

    setTotalPoints(points);
    setBreakdown(details);
    setRecommendations(generateRecommendations(points, formData));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScoreChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      englishScore: {
        ...prev.englishScore,
        [name]: value
      }
    }));
  };

  const handleReset = () => {
    setFormData({
      age: '',
      englishTest: '',
      englishScore: { listening: '', reading: '', writing: '', speaking: '' },
      skilledEmploymentAus: '',
      skilledEmploymentOverseas: '',
      educationLevel: '',
      studyInAustralia: false,
      australianStudyYears: '',
      specialistQualification: false,
      naatiCredential: false,
      professionalYear: false,
      regionalStudy: false,
      relationshipStatus: '',
      partnerEnglish: '',
      partnerSkillsAssessment: false,
      nominationType: ''
    });
    setTotalPoints(0);
    setBreakdown([]);
    setEnglishLevel('');
    setRecommendations([]);
    setActiveTab('1');
  };

  const getStatusColor = () => {
    if (totalPoints >= 85) return 'success';
    if (totalPoints >= 75) return 'info';
    if (totalPoints >= 65) return 'primary';
    return 'warning';
  };

  const getStatusMessage = () => {
    if (totalPoints >= 85) return 'Outstanding! Highly competitive score.';
    if (totalPoints >= 75) return 'Very Strong! Excellent invitation chances.';
    if (totalPoints >= 65) return 'Eligible! Meet minimum EOI requirement.';
    if (totalPoints >= 50) return 'Close! Just a few more points needed.';
    return 'Keep improving to reach 65 points.';
  };

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh' }}>
      {/* Banner Section */}
      <div style={{
        background: 'linear-gradient(135deg, #7D178F 0%, #a042b8 100%)',
        padding: '60px 0',
        marginBottom: '40px'
      }}>
        <Container>
          <Row className="mb-0">
            <Col>
              <div className="text-center text-white">
                <h1 className="display-4 fw-bold mb-2">  Skilled Migration Points Calculator</h1>
                <p className="lead">Comprehensive calculator for Australian skilled migration visas</p>
                <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
                  <Badge color="light" className="px-3 py-2" style={{ color: '#7D178F' }}>189 Skilled Independent</Badge>
                  <Badge color="light" className="px-3 py-2" style={{ color: '#7D178F' }}>190 State Nominated</Badge>
                  <Badge color="light" className="px-3 py-2" style={{ color: '#7D178F' }}>491 Regional</Badge>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="pb-5">
        <Row>
          <Col lg={8}>
            <Card className="shadow-lg border-0 rounded-3">
              <CardHeader className="bg-white border-0 pt-4 pb-0">
                <Nav tabs className="border-0">
                  <NavItem>
                    <NavLink
                      className={`${activeTab === '1' ? 'active text-white' : ''} px-4 py-2 border-0 rounded-top`}
                      onClick={() => setActiveTab('1')}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: activeTab === '1' ? '#7D178F' : 'transparent',
                        color: activeTab === '1' ? '#ffffff' : '#7D178F'
                      }}
                    >
                      Personal & English
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`${activeTab === '2' ? 'active text-white' : ''} px-4 py-2 border-0 rounded-top`}
                      onClick={() => setActiveTab('2')}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: activeTab === '2' ? '#7D178F' : 'transparent',
                        color: activeTab === '2' ? '#ffffff' : '#7D178F'
                      }}
                    >
                      Work & Education
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`${activeTab === '3' ? 'active text-white' : ''} px-4 py-2 border-0 rounded-top`}
                      onClick={() => setActiveTab('3')}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: activeTab === '3' ? '#7D178F' : 'transparent',
                        color: activeTab === '3' ? '#ffffff' : '#7D178F'
                      }}
                    >
                      Additional Factors
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>
              <CardBody className="p-4">
                <TabContent activeTab={activeTab}>
                  {/* Tab 1: Personal & English */}
                  <TabPane tabId="1">
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px', color: '#7D178F' }}>
                        👤 AGE
                      </h6>
                      <FormGroup>
                        <Label for="age" className="fw-semibold">Your Age Group</Label>
                        <Input
                          type="select"
                          name="age"
                          id="age"
                          value={formData.age}
                          onChange={handleChange}
                          className="form-select-lg"
                        >
                          <option value="">Select your age</option>
                          <option value="18-24">18-24 years (25 points)</option>
                          <option value="25-32">25-32 years (30 points) ⭐ Best</option>
                          <option value="33-39">33-39 years (25 points)</option>
                          <option value="40-44">40-44 years (15 points)</option>
                          <option value="45-49">45-49 years (0 points)</option>
                        </Input>
                      </FormGroup>
                    </div>

                    <div className="mb-4">
                      <h6 className="fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px', color: '#7D178F' }}>
                        🗣️ ENGLISH LANGUAGE TEST
                      </h6>
                      <FormGroup>
                        <Label for="englishTest" className="fw-semibold">Test Type</Label>
                        <Input
                          type="select"
                          name="englishTest"
                          id="englishTest"
                          value={formData.englishTest}
                          onChange={handleChange}
                          className="form-select-lg"
                        >
                          <option value="">Select test type</option>
                          <option value="ielts">IELTS Academic</option>
                          <option value="pte">PTE Academic</option>
                          <option value="toefl">TOEFL iBT</option>
                        </Input>
                      </FormGroup>

                      {formData.englishTest && (
                        <Card className="bg-light border-0 mt-3">
                          <CardBody className="p-3">
                            <p className="fw-semibold mb-3">Enter your test scores:</p>
                            <Row>
                              <Col md={6}>
                                <FormGroup>
                                  <Label className="small">Listening</Label>
                                  <Input
                                    type="number"
                                    name="listening"
                                    value={formData.englishScore.listening}
                                    onChange={handleScoreChange}
                                    placeholder={formData.englishTest === 'ielts' ? '6.0' : formData.englishTest === 'pte' ? '50' : '12'}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label className="small">Reading</Label>
                                  <Input
                                    type="number"
                                    name="reading"
                                    value={formData.englishScore.reading}
                                    onChange={handleScoreChange}
                                    placeholder={formData.englishTest === 'ielts' ? '6.0' : formData.englishTest === 'pte' ? '50' : '13'}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label className="small">Writing</Label>
                                  <Input
                                    type="number"
                                    name="writing"
                                    value={formData.englishScore.writing}
                                    onChange={handleScoreChange}
                                    placeholder={formData.englishTest === 'ielts' ? '6.0' : formData.englishTest === 'pte' ? '50' : '21'}
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label className="small">Speaking</Label>
                                  <Input
                                    type="number"
                                    name="speaking"
                                    value={formData.englishScore.speaking}
                                    onChange={handleScoreChange}
                                    placeholder={formData.englishTest === 'ielts' ? '6.0' : formData.englishTest === 'pte' ? '50' : '18'}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Alert color="info" className="mb-0 mt-2">
                              <small>
                                <strong>Competent:</strong> IELTS 6.0+ | PTE 50+ | TOEFL varies (0 pts)<br />
                                <strong>Proficient:</strong> IELTS 7.0+ | PTE 65+ | TOEFL varies (10 pts)<br />
                                <strong>Superior:</strong> IELTS 8.0+ | PTE 79+ | TOEFL varies (20 pts)
                              </small>
                            </Alert>
                          </CardBody>
                        </Card>
                      )}
                    </div>

                    <div className="d-flex justify-content-end">
                      <Button style={{ backgroundColor: '#7D178F', border: 'none' }} onClick={() => setActiveTab('2')}>
                        Next: Work & Education →
                      </Button>
                    </div>
                  </TabPane>

                  {/* Tab 2: Work & Education */}
                  <TabPane tabId="2">
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px', color: '#7D178F' }}>
                        💼 SKILLED EMPLOYMENT
                      </h6>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="skilledEmploymentAus" className="fw-semibold">Australian Experience</Label>
                            <Input
                              type="select"
                              name="skilledEmploymentAus"
                              id="skilledEmploymentAus"
                              value={formData.skilledEmploymentAus}
                              onChange={handleChange}
                              className="form-select-lg"
                            >
                              <option value="">Select</option>
                              <option value="none">None (0 pts)</option>
                              <option value="1-2">1-2 years (5 pts)</option>
                              <option value="3-4">3-4 years (10 pts)</option>
                              <option value="5-7">5-7 years (15 pts)</option>
                              <option value="8+">8+ years (20 pts)</option>
                            </Input>
                            <small className="text-muted">In nominated occupation</small>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="skilledEmploymentOverseas" className="fw-semibold">Overseas Experience</Label>
                            <Input
                              type="select"
                              name="skilledEmploymentOverseas"
                              id="skilledEmploymentOverseas"
                              value={formData.skilledEmploymentOverseas}
                              onChange={handleChange}
                              className="form-select-lg"
                            >
                              <option value="">Select</option>
                              <option value="none">None (0 pts)</option>
                              <option value="3-4">3-4 years (5 pts)</option>
                              <option value="5-7">5-7 years (10 pts)</option>
                              <option value="8+">8+ years (15 pts)</option>
                            </Input>
                            <small className="text-muted">In nominated occupation</small>
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>

                    <div className="mb-4">
                      <h6 className="fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px', color: '#7D178F' }}>
                        🎓 EDUCATION QUALIFICATION
                      </h6>
                      <FormGroup>
                        <Label for="educationLevel" className="fw-semibold">Highest Qualification</Label>
                        <Input
                          type="select"
                          name="educationLevel"
                          id="educationLevel"
                          value={formData.educationLevel}
                          onChange={handleChange}
                          className="form-select-lg"
                        >
                          <option value="">Select qualification</option>
                          <option value="diploma">Diploma or Trade (10 pts)</option>
                          <option value="bachelor">Bachelor Degree (15 pts)</option>
                          <option value="masters-doctorate">Masters/Doctorate (20 pts)</option>
                        </Input>
                      </FormGroup>

                      <FormGroup check className="mt-3">
                        <Label check className="d-flex align-items-center">
                          <Input
                            type="checkbox"
                            name="studyInAustralia"
                            checked={formData.studyInAustralia}
                            onChange={handleChange}
                            className="me-2"
                          />
                          <span>Studied in Australia</span>
                        </Label>
                      </FormGroup>

                      {formData.studyInAustralia && (
                        <FormGroup className="mt-2 ms-4">
                          <Label for="australianStudyYears" className="small">Study Duration</Label>
                          <Input
                            type="select"
                            name="australianStudyYears"
                            id="australianStudyYears"
                            value={formData.australianStudyYears}
                            onChange={handleChange}
                          >
                            <option value="">Select</option>
                            <option value="1">Less than 2 years</option>
                            <option value="2+">2+ years (5 pts)</option>
                          </Input>
                        </FormGroup>
                      )}

                      <FormGroup check className="mt-3">
                        <Label check className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <Input
                              type="checkbox"
                              name="specialistQualification"
                              checked={formData.specialistQualification}
                              onChange={handleChange}
                              className="me-2"
                            />
                            <div>
                              <span className="fw-semibold">Specialist Education</span>
                              <small className="d-block text-muted">Masters/PhD in STEM from Australian institution</small>
                            </div>
                          </div>
                          <Badge color="success">+10</Badge>
                        </Label>
                      </FormGroup>
                    </div>

                    <div className="d-flex justify-content-between">
                      <Button color="secondary" outline onClick={() => setActiveTab('1')}>
                        ← Back
                      </Button>
                      <Button style={{ backgroundColor: '#7D178F', border: 'none' }} onClick={() => setActiveTab('3')}>
                        Next: Additional Factors →
                      </Button>
                    </div>
                  </TabPane>

                  {/* Tab 3: Additional Factors */}
                  <TabPane tabId="3">
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px', color: '#7D178F' }}>
                        ⭐ OTHER QUALIFICATIONS
                      </h6>
                      <Card className="bg-light border-0">
                        <CardBody className="p-3">
                          <FormGroup check className="mb-3">
                            <Label check className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <Input
                                  type="checkbox"
                                  name="naatiCredential"
                                  checked={formData.naatiCredential}
                                  onChange={handleChange}
                                  className="me-2"
                                />
                                <div>
                                  <span className="fw-semibold">NAATI Credential</span>
                                  <small className="d-block text-muted">Accredited community language credential</small>
                                </div>
                              </div>
                              <Badge color="success">+5</Badge>
                            </Label>
                          </FormGroup>

                          <FormGroup check className="mb-3">
                            <Label check className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <Input
                                  type="checkbox"
                                  name="professionalYear"
                                  checked={formData.professionalYear}
                                  onChange={handleChange}
                                  className="me-2"
                                />
                                <div>
                                  <span className="fw-semibold">Professional Year</span>
                                  <small className="d-block text-muted">In Australia (IT, Accounting, Engineering)</small>
                                </div>
                              </div>
                              <Badge color="success">+5</Badge>
                            </Label>
                          </FormGroup>

                          <FormGroup check className="mb-0">
                            <Label check className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <Input
                                  type="checkbox"
                                  name="regionalStudy"
                                  checked={formData.regionalStudy}
                                  onChange={handleChange}
                                  className="me-2"
                                />
                                <div>
                                  <span className="fw-semibold">Regional Study</span>
                                  <small className="d-block text-muted">2+ years in regional Australia</small>
                                </div>
                              </div>
                              <Badge color="success">+5</Badge>
                            </Label>
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </div>

                    <div className="mb-4">
                      <h6 className="fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px', color: '#7D178F' }}>
                        👥 RELATIONSHIP STATUS
                      </h6>
                      <Card className="bg-light border-0">
                        <CardBody className="p-3">
                          <FormGroup check className="mb-3">
                            <Label check className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <Input
                                  type="checkbox"
                                  name="professionalYear"
                                  checked={formData.professionalYear}
                                  onChange={handleChange}
                                  className="me-2"
                                />
                                <div>
                                  <span className="fw-semibold">Professional Year</span>
                                  <small className="d-block text-muted">In Australia (IT, Accounting, Engineering)</small>
                                </div>
                              </div>
                              <Badge color="success">+5</Badge>
                            </Label>
                          </FormGroup>

                          <FormGroup check className="mb-0">
                            <Label check className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <Input
                                  type="checkbox"
                                  name="regionalStudy"
                                  checked={formData.regionalStudy}
                                  onChange={handleChange}
                                  className="me-2"
                                />
                                <div>
                                  <span className="fw-semibold">Regional Study</span>
                                  <small className="d-block text-muted">2+ years in regional Australia</small>
                                </div>
                              </div>
                              <Badge color="success">+5</Badge>
                            </Label>
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </div>

                    <div className="mb-4">
                      <h6 className="text-uppercase text-primary fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                        👥 RELATIONSHIP STATUS
                      </h6>
                      <FormGroup>
                        <Label for="relationshipStatus" className="fw-semibold">Current Status</Label>
                        <Input
                          type="select"
                          name="relationshipStatus"
                          id="relationshipStatus"
                          value={formData.relationshipStatus}
                          onChange={handleChange}
                          className="form-select-lg"
                        >
                          <option value="">Select status</option>
                          <option value="single">Single (10 pts)</option>
                          <option value="partner">Have partner/spouse</option>
                          <option value="partner-no-migration">Partner not migrating (0 pts)</option>
                        </Input>
                      </FormGroup>

                      {formData.relationshipStatus === 'partner' && (
                        <Card className="bg-light border-0 mt-3">
                          <CardBody className="p-3">
                            <FormGroup>
                              <Label for="partnerEnglish" className="small fw-semibold">Partner's English</Label>
                              <Input
                                type="select"
                                name="partnerEnglish"
                                id="partnerEnglish"
                                value={formData.partnerEnglish}
                                onChange={handleChange}
                              >
                                <option value="">Select</option>
                                <option value="none">No competent English</option>
                                <option value="competent">Competent English</option>
                              </Input>
                            </FormGroup>

                            {formData.partnerEnglish === 'competent' && (
                              <FormGroup check className="mt-2">
                                <Label check className="d-flex align-items-center">
                                  <Input
                                    type="checkbox"
                                    name="partnerSkillsAssessment"
                                    checked={formData.partnerSkillsAssessment}
                                    onChange={handleChange}
                                    className="me-2"
                                  />
                                  <span className="small">Partner has positive skills assessment (10 pts total)</span>
                                </Label>
                              </FormGroup>
                            )}
                          </CardBody>
                        </Card>
                      )}
                    </div>

                    <div className="mb-4">
                      <h6 className="text-uppercase text-primary fw-bold mb-3" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                        🏛️ NOMINATION/SPONSORSHIP
                      </h6>
                      <FormGroup>
                        <Label for="nominationType" className="fw-semibold">Nomination Type</Label>
                        <Input
                          type="select"
                          name="nominationType"
                          id="nominationType"
                          value={formData.nominationType}
                          onChange={handleChange}
                          className="form-select-lg"
                        >
                          <option value="">Select option</option>
                          <option value="none">No nomination - Subclass 189 (0 pts)</option>
                          <option value="state-190">State nomination - Subclass 190 (5 pts)</option>
                          <option value="state-491">State/Territory - Subclass 491 (15 pts)</option>
                          <option value="family-491">Family sponsor - Subclass 491 (15 pts)</option>
                        </Input>
                      </FormGroup>
                    </div>

                    <div className="d-flex justify-content-between">
                      <Button color="secondary" outline onClick={() => setActiveTab('2')}>
                        ← Back
                      </Button>
                      <Button
                        color="primary"
                        size="lg"
                        onClick={calculatePoints}
                        style={{ background: 'linear-gradient(135deg, #4e54c8 0%, #8f94fb 100%)', border: 'none' }}
                      >
                        Calculate My Points 🎯
                      </Button>
                    </div>
                  </TabPane>
                </TabContent>

                <div className="mt-4 pt-3 border-top">
                  <Button color="danger" outline onClick={handleReset} className="w-100">
                    Reset All & Start Over
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Sticky Results Panel */}
          <Col lg={4}>
            <div style={{ position: 'sticky', top: '20px' }}>
              <Card className="shadow-lg border-0 rounded-3 overflow-hidden">
                <div
                  className="text-white text-center py-4"
                  style={{
                    background: totalPoints >= 65
                      ? 'linear-gradient(135deg, #4e54c8 0%, #8f94fb 100%)'
                      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                  }}
                >
                  <div className="display-1 fw-bold mb-2">{totalPoints}</div>
                  <div className="h5 mb-0">Total Points</div>
                </div>

                <CardBody className="p-4">
                  {totalPoints > 0 ? (
                    <>
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted fw-semibold">Your Progress</small>
                          <small className="fw-bold">{Math.min(100, totalPoints)}%</small>
                        </div>
                        <Progress
                          value={Math.min(100, totalPoints)}
                          color={getStatusColor()}
                          style={{ height: '10px' }}
                          className="rounded-pill"
                        />
                        <small className="text-muted d-block mt-2">{getStatusMessage()}</small>
                      </div>

                      <Alert
                        color={totalPoints >= 65 ? 'success' : 'warning'}
                        className="mb-4 border-0"
                      >
                        <div className="d-flex align-items-start">
                          <span className="me-2" style={{ fontSize: '1.5rem' }}>
                            {totalPoints >= 65 ? ' ' : '⚠️'}
                          </span>
                          <div>
                            <div className="fw-bold mb-1">
                              {totalPoints >= 65 ? 'EOI Eligible!' : 'Not Yet Eligible'}
                            </div>
                            <small>
                              {totalPoints >= 65
                                ? 'You can submit Expression of Interest'
                                : `Need ${65 - totalPoints} more points`}
                            </small>
                          </div>
                        </div>
                      </Alert>

                      {/* Points Breakdown */}
                      {breakdown.length > 0 && (
                        <div className="mb-4">
                          <h6 className="fw-bold mb-3">Points Breakdown</h6>
                          <div className="border rounded-3 overflow-hidden">
                            <Table className="mb-0" size="sm">
                              <tbody>
                                {breakdown.map((item, index) => (
                                  <tr key={index} style={{ borderBottom: index < breakdown.length - 1 ? '1px solid #dee2e6' : 'none' }}>
                                    <td className="py-2 ps-3">
                                      <small>
                                        <span className="me-1">{item.icon}</span>
                                        <span className="fw-semibold">{item.category}</span>
                                      </small>
                                    </td>
                                    <td className="text-end py-2 pe-3">
                                      <Badge color="primary" pill>{item.points}</Badge>
                                    </td>
                                  </tr>
                                ))}
                                <tr className="bg-light">
                                  <td className="py-2 ps-3">
                                    <strong>Total</strong>
                                  </td>
                                  <td className="text-end py-2 pe-3">
                                    <Badge color="success" pill className="px-3">{totalPoints}</Badge>
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {/* Recommendations */}
                      {recommendations.length > 0 && (
                        <div>
                          <h6 className="fw-bold mb-3">💡 Recommendations</h6>
                          {recommendations.map((rec, index) => (
                            <Alert
                              key={index}
                              color={rec.priority === 'high' ? 'info' : 'secondary'}
                              className="py-2 px-3 mb-2"
                            >
                              <div className="fw-semibold small mb-1">{rec.title}</div>
                              <small className="text-muted">{rec.description}</small>
                            </Alert>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <div style={{ fontSize: '4rem', opacity: 0.3 }}>📊</div>
                      <p className="text-muted mb-0">Complete the form to see your points</p>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card className="mt-3 border-0 bg-white shadow-sm">
                <CardBody className="p-3">
                  <h6 className="fw-bold mb-2 small">📌 Quick Tips</h6>
                  <ul className="mb-0 small text-muted ps-3">
                    <li className="mb-1">65 points minimum for EOI</li>
                    <li className="mb-1">75+ points more competitive</li>
                    <li className="mb-1">Valid skills assessment required</li>
                    <li className="mb-0">Points at invitation matter</li>
                  </ul>
                </CardBody>
              </Card>

              <Card className="mt-3 border-0 bg-white shadow-sm">
                <CardBody className="p-3">
                  <small className="text-muted">
                    <strong>⚠️ Important:</strong> This calculator is for guidance only. For professional migration advice, consult a registered MARA agent.
                  </small>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}